/**
 * Quiz base component for handling multi-step quizzes.
 * @extends HTMLElement
 */
class Quiz extends HTMLElement {
  constructor() {
    super();

    this.ERROR_CLASS = "quiz__error--visible";
    this.STEP_COMPLETE_CLASS = "quiz__step--complete";
    this.ANSWER_VALUE_CLASS = "quiz__answer-value";
    this.ANSWER_PLACEHOLDER = "[value]";
    this.QUIZ_COMPLETE_CLASS = "quiz--complete";
    this.QUIZ_EMPTY_CLASS = "quiz-email--empty";

    this.selectedOptions = [];

    this.addListeners();
  }

  /**
   * Adds event listeners for quiz navigation and actions.
   */
  addListeners() {
    this.backButton.forEach(button => button.addEventListener("click", event => this.stepChange(event)));
    this.nextButton.forEach(button => button.addEventListener("click", event => this.stepChange(event)));
    this.resetButton?.addEventListener("click", () => this.resetQuiz());
    this.form?.addEventListener("submit", event => this.handleSubmit(event));

    subscribe(PUB_SUB_EVENTS.accordionClosed, step => this.handleStepClose(step.element));
  }

  /**
   * Resets the quiz to its initial state.
   */
  resetQuiz() {
    const firstStep = this.steps[0].querySelector("[data-quiz-button]")

    this.form.reset();
    this.selectedOptions = [];
    this.steps.forEach(step => step.classList.remove(this.STEP_COMPLETE_CLASS));
    this.classList.remove(this.QUIZ_COMPLETE_CLASS);

    this.scrollToFirstStep();
    this.publishAccordionEvent(firstStep);
  }

  /**
   * Scrolls to the first quiz step.
   */
  scrollToFirstStep() {
    const step = this.steps[0];
    const headerHeight = document.querySelector("[data-header]")?.offsetHeight || 0;
    const stepPosition = step.getBoundingClientRect().top + window.scrollY - headerHeight;

    window.scrollTo({ top: stepPosition, behavior: "smooth" });
  }

  /**
   * Handles closing of a quiz step.
   * @param {HTMLElement} step
   */
  handleStepClose(step) {
    if (!step.hasAttribute("data-quiz-step")) return;

    const inputs = step.querySelectorAll("[data-quiz-input]");

    if (!this.isValidStep(step, inputs)) return;

    this.updateStep(step, false);
  }

  /**
   * Handles step navigation.
   * @param {Event} event
   */
  stepChange({ target }) {
    const step = target.closest('[data-quiz-step]');
    const inputs = step.querySelectorAll("[data-quiz-input]");
    const isBack = target.hasAttribute("data-quiz-back");

    let stepIndex = step.dataset.quizStep;

    isBack ? stepIndex-- : stepIndex++;

    const targetStep = this.steps[stepIndex];
    const stepTrigger = targetStep.querySelector("[data-quiz-button]");

    if (isBack && stepTrigger) this.publishAccordionEvent(stepTrigger);
    if (!this.isValidStep(step, inputs)) return;

    this.updateStep(step);

    if (stepTrigger) this.publishAccordionEvent(stepTrigger);
  }

  /**
   * Validates a quiz step.
   * @param {HTMLElement} step
   * @param {NodeListOf<Element>} inputs
   * @returns {boolean}
   */
  isValidStep(step, inputs) {
    const inputsArray = Array.from(inputs);

    let isValid = inputsArray.some(input => input.checked || input.value);

    this.setError(step, !isValid);

    return isValid;
  }

  /**
   * Sets error state for a step.
   * @param {HTMLElement} step
   * @param {boolean} hasError
   */
  setError(step, hasError) {
    const errorMessage = step.querySelector("[data-quiz-error]");

    hasError
      ? errorMessage.classList.add(this.ERROR_CLASS)
      : errorMessage.classList.remove(this.ERROR_CLASS);
  }

  /**
   * Updates a quiz step's completion state and answer value.
   * @param {HTMLElement} step
   * @param {boolean} [isComplete=true]
   */
  updateStep(step, isComplete = true) {
    const stepAnswer = step.querySelector("[data-quiz-answer]");
    const hasPlaceholder = stepAnswer.textContent.includes(this.ANSWER_PLACEHOLDER);

    hasPlaceholder
      ? this.setAnswerValueText(step, stepAnswer)
      : this.replaceAnswerValueText(step, stepAnswer);

    if (isComplete) step.classList.add(this.STEP_COMPLETE_CLASS);
  }

  /**
   * Sets the answer value text for a step.
   * @param {HTMLElement} step
   * @param {HTMLElement} stepAnswer
   */
  setAnswerValueText(step, stepAnswer) {
    const selectedValue = this.getInputValue(step);
    const currency = this.getStepCurrency(step);
    const answerValueHtml = `
      <span
        class="${this.ANSWER_VALUE_CLASS}"
        data-quiz-answer-value
      >
        ${currency}${selectedValue}
      </span>
    `

    stepAnswer.innerHTML = stepAnswer.textContent.replace(this.ANSWER_PLACEHOLDER, answerValueHtml);
  }

  /**
   * Replaces the answer value text for a step.
   * @param {HTMLElement} step
   * @param {HTMLElement} stepAnswer
   */
  replaceAnswerValueText(step, stepAnswer) {
    const answerValue = stepAnswer.querySelector("[data-quiz-answer-value]");
    const currency = this.getStepCurrency(step);

    if (answerValue) answerValue.textContent = `${currency}${this.getInputValue(step)}`;
  }

  /**
   * Gets the currency for the step if applicable.
   * @param {HTMLElement} step
   * @returns {string}
   */
  getStepCurrency(step) {
    return step.hasAttribute("data-quiz-step-currency") && this.currency ? this.currency : "";
  }

  /**
   * Gets the selected input value for a step.
   * @param {HTMLElement} step
   * @returns {string}
   */
  getInputValue(step) {
    const inputs = step.querySelectorAll("[data-quiz-input]");

    let selectedValue = "";

    inputs.forEach(input => {
      if (input.type !== "text" && input.type !== "range" && !input.checked) return;

      selectedValue += `${input.value}${input.type === "checkbox" ? ", " : ""}`;
    });

    return selectedValue.trim().replace(/,$/, "");
  }

  /**
   * Handles quiz form submission.
   * @param {Event} event
   */
  handleSubmit(event) {
    event.preventDefault();

    const isValid = this.isValidQuiz();
    const step = this.steps[this.steps.length - 1];
    const stepTrigger = step.querySelector("[data-quiz-button]");

    if (isValid) {
      step.classList.add(this.STEP_COMPLETE_CLASS);

      this.publishAccordionEvent(stepTrigger);
      this.getFormData();
    }
  }

  /**
   * Validates the entire quiz.
   * @returns {boolean}
   */
  isValidQuiz() {
    return Array.from(this.steps).every(step => {
      const inputs = step.querySelectorAll("[data-quiz-input]");

      return this.isValidStep(step, inputs);
    });
  }

  /**
   * Collects form data from the quiz.
   */
  getFormData() {
    const formData = new FormData(this.form);

    this.selectedOptions = Array.from(formData.values());
  }

  /**
   * Shows quiz results.
   */
  showResults() {
    this.classList.add(this.QUIZ_COMPLETE_CLASS);
  }

  /**
   * Shows empty quiz results.
   */
  showEmptyResults() {
    this.classList.add(this.QUIZ_COMPLETE_CLASS);
    this.emailBlock.classList.add(this.QUIZ_EMPTY_CLASS)
  }

  /**
   * Publishes an accordion event for a step.
   * @param {HTMLElement} button
   */
  publishAccordionEvent(button) {
    const item = button.closest("[data-accordion-item]");

    publish(PUB_SUB_EVENTS.toggleAccordion, { item, button });
  }

  /**
   * Builds a JSON-serialisable object containing all quiz questions and answers.
   * Subclasses should call super.buildQuizData() and extend the result.
   * @returns {{ questions: Array<{ question: string, answer: string }> }}
   */
  buildQuizData() {
    const questions = Array.from(this.steps).map(step => ({
      question: this.getStepText(step, "[data-quiz-question]"),
      answer: this.getStepText(step, "[data-quiz-answer]")
    }));

    return { questions };
  }

  /**
   * Gets trimmed text content for a selector within a step.
   * @param {HTMLElement} step
   * @param {string} selector
   * @returns {string}
   */
  getStepText(step, selector) {
    return step.querySelector(selector)?.textContent?.replace(/\s+/g, " ").trim() || "";
  }

  /**
   * Serializes quiz data to JSON and writes it into the hidden input.
   */
  populateHiddenInput() {
    if (!this.emailForm) return;
    if (!this.hiddenInput) return;

    this.hiddenInput.value = JSON.stringify(this.buildQuizData());

    this.hiddenInput.dispatchEvent(new Event("input", { bubbles: true }));
  }

  styleForm() {
    const stylesheet = document.createElement("style");

    stylesheet.textContent = `
      :host {
        --inline-container-max-width: 100%;
        --form-field-border-radius: 0;
        --form-background-color: transparent;
        --button-border-radius: 100px !important;
        --forms-heading-font: normal 700 22px Futura PT, sans-serif;
        --forms-body-font: normal 400 14px Futura PT, sans-serif;
       
        input {
          box-shadow: none !important;
          border-bottom: 0.1rem solid var(--color-primary) !important;
          font-family: var(--font-body-family) !important;
          text-transform: capitalise !important;:
        }
        
        label {
          font-family: var(--font-body-family) !important;
          font-size: var(--text-size-base) !important;
          top: var(--input-label-top) !important;
        }
        
        form > div:has(textarea) {
          display: none !important;
        }
        
        button {
          align-items: center !important;
          font-family: var(--font-body-family) !important;
          font-size: var(--text-size-button-sm) !important;
          font-weight: var(--font-weight-bold) !important;
          height: 3.9rem !important;
          margin-left: auto !important;
          margin-top: var(--space-4) !important;
          padding-block: 0 !important;
          text-transform: uppercase !important;
          width: fit-content !important;
        }
        
        @media (width > 750px) {
          button {
            font-size: var(--text-size-button-base) !important;
            height: var(--space-24) !important;
            margin-top: var(--space-6) !important;
          }
        }
      }
    `

    this.shadowRoot?.append(stylesheet);
  }

  /**
   * Returns the quiz form element.
   * @returns {HTMLFormElement}
   */
  get form() {
    return this.querySelector("[data-quiz-form]");
  }

  /**
   * Returns all quiz step elements.
   * @returns {NodeListOf<Element>}
   */
  get steps() {
    return this.querySelectorAll("[data-quiz-step]");
  }

  /**
   * Returns all quiz input elements.
   * @returns {NodeListOf<Element>}
   */
  get inputs() {
    return this.querySelectorAll("[data-quiz-input]");
  }

  /**
   * Returns all back button elements for quiz navigation.
   * @returns {NodeListOf<Element>}
   */
  get backButton() {
    return this.querySelectorAll("[data-quiz-back]");
  }

  /**
   * Returns all next button elements for quiz navigation.
   * @returns {NodeListOf<Element>}
   */
  get nextButton() {
    return this.querySelectorAll("[data-quiz-next]");
  }

  /**
   * Returns the submit button element for the quiz.
   * @returns {HTMLButtonElement}
   */
  get submitButton() {
    return this.querySelector("[data-quiz-submit]");
  }

  /**
   * Returns the reset button element for the quiz.
   * @returns {HTMLButtonElement}
   */
  get resetButton() {
    return this.querySelector("[data-quiz-reset]");
  }

  /**
   * Returns the email block element containing the form.
   * @returns {Element}
   */
  get emailBlock() {
    return this.querySelector("[data-quiz-email]");
  }

  /**
   * Returns the hidden input element used to pass quiz data to the form.
   * @returns {HTMLInputElement}
   */
  get emailForm() {
    return this.querySelector("[data-quiz-email-form]");
  }

  get shadowRoot() {
    return this.emailForm?.querySelector("shopify-forms-embed")?.shadowRoot;
  }

  get hiddenInput() {
    return this.shadowRoot?.querySelector("textarea");
  }
}

if (!customElements.get("quiz-component")) {
  customElements.define("quiz-component", Quiz);
}

/**
 * Quiz component for product specifier quizzes.
 * @extends Quiz
 */
class SpecifierQuiz extends Quiz {
  constructor() {
    super();

    /** @type {Array} */
    this.products = JSON.parse(this.querySelector("[data-quiz-product-options]").textContent);
  }

  /**
   * Returns the product URL for a given product.
   * @param {Object} product
   * @returns {string}
   */
  productUrl(product) {
    return `/products/${product.id}?section_id=card-product`;
  }

  /**
   * Collects form data and fetches product cards.
   */
  getFormData() {
    super.getFormData();

    this.results = this.collectResults();

    if (this.results.length > 0) this.fetchProductCards();
    else this.showEmptyResults();
  }

  /**
   * Collects matching products based on selected options.
   * @returns {Array}
   */
  collectResults() {
    return this.products
      .map(product => { return { product, matchCount: this.findOptionMatchCount(product) }})
      .filter(item => item.matchCount > 0)
      .sort((a, b) => b.matchCount - a.matchCount)
      .slice(0, 3)
      .map(item => item.product);
  }

  /**
   * Finds the match count for a product.
   * @param {Object} product
   * @returns {number}
   */
  findOptionMatchCount(product) {
    return product.options.some(option => this.selectedOptions.includes(option));
  }

  /**
   * Fetches product cards for matched products.
   */
  fetchProductCards() {
    this.results.forEach(product =>
      fetch(this.productUrl(product))
        .then(response => response.text())
        .then(responseText => this.renderCard(responseText))
        .then(() => this.styleForm())
        .then(() => this.populateHiddenInput())
        .finally(() => this.showResults())
    );
  }

  /**
   * Renders a product card.
   * @param {string} responseText
   */
  renderCard(responseText) {
    const cardHtml = new DOMParser().parseFromString(responseText, "text/html");
    const card = cardHtml.querySelector("[data-card-product]");

    this.productsContainer.appendChild(card);
  }

  /**
   * Builds quiz data including the matched products.
   * @returns {{ questions: Array<{ question: string, answer: string }>, products: string[] }}
   */
  buildQuizData() {
    console.log(this.results);
    return {
      ...super.buildQuizData(),
      products: this.results.map(product => {
        return {
          title: product.title,
          url: `${window.shopUrl}${this.productUrl(product)}`,
          price: product.price,
        }
      })
    };
  }

  /**
   * Gets the products container element.
   * @returns {HTMLElement}
   */
  get productsContainer() {
    return this.querySelector("[data-quiz-products]");
  }
}

if (!customElements.get("specifier-quiz")) {
  customElements.define("specifier-quiz", SpecifierQuiz);
}

/**
 * Quiz component for dispenser calculator.
 * @extends Quiz
 */
class DispenserCalculator extends Quiz {
  constructor() {
    super();

    /** @type {string} */
    this.currency = this.querySelector("[data-quiz-currency-input]")?.value || "£";
    this.calculatedStepValues = {};
  }

  /**
   * Adds listeners for currency updates.
   */
  addListeners() {
    super.addListeners();

    subscribe(PUB_SUB_EVENTS.optionSelected, event => this.updateCurrency(event));
  }

  resetQuiz() {
    super.resetQuiz();

    this.calculatedStepValues = {};
  }

  /**
   * Updates the currency for the quiz.
   * @param {Object} event
   */
  updateCurrency({ id, value }) {
    if (id !== this.id) return;

    this.currency = value;
    this.updateRangeInputMarkers();
    this.updateStepCompleteAnswers();
  }

  /**
   * Updates currency markers for range inputs.
   */
  updateRangeInputMarkers() {
    const markers = this.querySelectorAll("[data-quiz-marker]");

    markers.forEach(marker => this.updateRangeInputMarkerCurrency(marker));
  }

  /**
   * Updates the currency for a range input marker.
   * @param {HTMLElement} marker
   */
  updateRangeInputMarkerCurrency(marker) {
    const markerCurrency = marker.querySelector("[data-quiz-marker-currency]");

    if (markerCurrency) markerCurrency.textContent = this.currency;
  }

  /**
   * Updates answer values for completed steps with currency.
   */
  updateStepCompleteAnswers() {
    this.steps.forEach(step => {
      if (!step.classList.contains(this.STEP_COMPLETE_CLASS)) return;
      if (!step.hasAttribute("data-quiz-step-currency")) return;

      const stepAnswerValue = step.querySelector("[data-quiz-answer-value]");
      const newStepAnswer = `${this.currency}${stepAnswerValue.textContent.replace(/^\D*/, "")}`;

      this.replaceAnswerValueText(step, newStepAnswer);
    });
  }

  /**
   * Collects form data and calculates savings.
   */
  getFormData() {
    super.getFormData();

    this.calculateStepValues();

    this.finalValue = this.calculateFinalValue();

    if (this.finalValue) {
      this.updateResultsDisplay();
      this.styleForm();
      this.populateHiddenInput();
      this.showResults();

      this.calculatedStepValues = {};
    }
  }

  /**
   * Updates the results display with the calculated savings value.
   */
  updateResultsDisplay() {
    this.calculationValuePlaceholder.innerHTML = `${this.currency}${this.finalValue.toFixed(2)}`;
  }

  /**
   * Returns an array of numeric input values from the form, in order.
   * @returns {number[]}
   */
  getNumericInputValues() {
    return Array.from(this.selectedOptions).map(value => Number(value));
  }

  /**
   * Evaluates a formula string by replacing [stepX] with processed or input values.
   * @param {string} formula
   * @returns {number}
   */
  evaluateFormula(formula) {
    const replaced = formula.replace(/\[step(\d+)]/g, (_, n) => {
      return this.calculatedStepValues[`step${n}`]
        ? this.calculatedStepValues[`step${n}`]
        : this.getNumericInputValues()[n - 1];
    });

    return Number(Function(`return (${replaced})`)());
  }

  /**
   * Calculates the value for a step using its formula or input value.
   * @param {number} stepIndex
   * @param {HTMLElement} step
   * @returns {number}
   */
  calculateStepValue(stepIndex, step) {
    const formula = step.getAttribute('data-quiz-step-formula');

    if (!formula) return this.getNumericInputValues()[stepIndex - 1];

    return this.evaluateFormula(formula);
  }

  calculateStepValues() {
    this.steps.forEach((step, index) => {
      const stepIndex = index + 1;

      this.calculatedStepValues[`step${stepIndex}`] = this.calculateStepValue(stepIndex, step);
    });
  }

  /**
   * Calculates the final value using the final formula or sums all step values.
   * @returns {number}
   */
  calculateFinalValue() {
    const finalFormula = this.getAttribute('data-quiz-final-formula');

    if (finalFormula) return this.evaluateFormula(finalFormula);
  }

  /**
   * Builds quiz data including the calculated savings value and currency.
   * @returns {{ questions: Array<{ question: string, answer: string }>, savings: string, currency: string }}
   */
  buildQuizData() {
    return {
      ...super.buildQuizData(),
      savings: this.finalValue ? `${this.currency}${this.finalValue.toFixed(2)}` : null
    };
  }

  /**
   * Gets the total price element.
   * @returns {HTMLElement}
   */
  get calculationValuePlaceholder() {
    return this.querySelector("[data-quiz-calculation-value]");
  }
}

if (!customElements.get("dispenser-calculator")) {
  customElements.define("dispenser-calculator", DispenserCalculator);
}